import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { AgriculturalDefensive, Fertilizer, Manufacturer } from '../types';
import { getFertilizerCategoryLabel } from '../constants/fertilizer.constants';

const HEAD_COLOR: [number, number, number] = [90, 106, 207];
const ALT_COLOR: [number, number, number] = [245, 245, 245];

const DEFENSIVE_HEADERS = ['Nome Comercial', 'Fabricante', 'Classe', 'Natureza', 'Origem'];

const defensiveRows = (
	items: AgriculturalDefensive[],
	resolveUserName: (id: string | null) => string
) =>
	items.map((d) => [
		d.commercialName ?? '-',
		d.manufacturer?.tradingName ?? d.manufacturer?.businessName ?? '-',
		(Array.isArray(d.class) ? d.class.join(', ') : d.class) || '-',
		d.nature ?? '-',
		resolveUserName(d.userId),
	]);

export const exportDefensivesToExcel = (
	items: AgriculturalDefensive[],
	resolveUserName: (id: string | null) => string
) => {
	const ws = XLSX.utils.aoa_to_sheet([DEFENSIVE_HEADERS, ...defensiveRows(items, resolveUserName)]);
	ws['!cols'] = [{ wch: 24 }, { wch: 20 }, { wch: 16 }, { wch: 12 }, { wch: 16 }];
	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, 'Defensivos');
	XLSX.writeFile(wb, 'defensivos.xlsx');
};

export const exportDefensivesToPdf = (
	items: AgriculturalDefensive[],
	resolveUserName: (id: string | null) => string
) => {
	const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
	doc.setFontSize(16);
	doc.text('Defensivos Agrícolas', 14, 15);
	autoTable(doc, {
		startY: 22,
		head: [DEFENSIVE_HEADERS],
		body: defensiveRows(items, resolveUserName),
		styles: { fontSize: 9, cellPadding: 2 },
		headStyles: { fillColor: HEAD_COLOR, textColor: 255, fontStyle: 'bold' },
		alternateRowStyles: { fillColor: ALT_COLOR },
	});
	doc.save('defensivos.pdf');
};

const FERTILIZER_HEADERS = ['Nome', 'Fabricante', 'Categoria', 'Natureza', 'Origem'];

const fertilizerRows = (
	items: Fertilizer[],
	resolveUserName: (id: string | null) => string
) =>
	items.map((f) => [
		f.name,
		f.manufacturer?.tradingName ?? f.manufacturer?.businessName ?? '-',
		getFertilizerCategoryLabel(f.category),
		f.nature,
		resolveUserName(f.owner ?? null),
	]);

export const exportFertilizersToExcel = (
	items: Fertilizer[],
	resolveUserName: (id: string | null) => string
) => {
	const ws = XLSX.utils.aoa_to_sheet([FERTILIZER_HEADERS, ...fertilizerRows(items, resolveUserName)]);
	ws['!cols'] = [{ wch: 24 }, { wch: 20 }, { wch: 16 }, { wch: 12 }, { wch: 16 }];
	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, 'Fertilizantes');
	XLSX.writeFile(wb, 'fertilizantes.xlsx');
};

export const exportFertilizersToPdf = (
	items: Fertilizer[],
	resolveUserName: (id: string | null) => string
) => {
	const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
	doc.setFontSize(16);
	doc.text('Fertilizantes', 14, 15);
	autoTable(doc, {
		startY: 22,
		head: [FERTILIZER_HEADERS],
		body: fertilizerRows(items, resolveUserName),
		styles: { fontSize: 9, cellPadding: 2 },
		headStyles: { fillColor: HEAD_COLOR, textColor: 255, fontStyle: 'bold' },
		alternateRowStyles: { fillColor: ALT_COLOR },
	});
	doc.save('fertilizantes.pdf');
};

const MANUFACTURER_HEADERS = ['Nome Fantasia', 'Razão Social', 'CNPJ', 'Tipo'];

const manufacturerRows = (items: Manufacturer[]) =>
	items.map((m) => [
		m.tradingName ?? '-',
		m.businessName ?? '-',
		m.cnpj ?? '-',
		m.isPublic ? 'Público' : 'Privado',
	]);

export const exportManufacturersToExcel = (items: Manufacturer[]) => {
	const ws = XLSX.utils.aoa_to_sheet([MANUFACTURER_HEADERS, ...manufacturerRows(items)]);
	ws['!cols'] = [{ wch: 24 }, { wch: 24 }, { wch: 18 }, { wch: 10 }];
	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, 'Fabricantes');
	XLSX.writeFile(wb, 'fabricantes.xlsx');
};

export const exportManufacturersToPdf = (items: Manufacturer[]) => {
	const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
	doc.setFontSize(16);
	doc.text('Fabricantes', 14, 15);
	autoTable(doc, {
		startY: 22,
		head: [MANUFACTURER_HEADERS],
		body: manufacturerRows(items),
		styles: { fontSize: 9, cellPadding: 2 },
		headStyles: { fillColor: HEAD_COLOR, textColor: 255, fontStyle: 'bold' },
		alternateRowStyles: { fillColor: ALT_COLOR },
	});
	doc.save('fabricantes.pdf');
};
